import type { MotionRecord } from '@cartwhl/common/api/getMotionsByGroup';
import type { Job, Variation, VariationOutput } from '@cartwhl/common/types';

import {
  parseMotionExportURLSlots,
  previewBvhUrlsFromExportSlots,
} from './batchMotionExportUrls';

/** Count playable body outputs on a motion record. */
export function countMotionRecordOutputs(motion: MotionRecord): number {
  const variationOutputCount = countVariationOutputSlots(motion.outputs);
  if (variationOutputCount > 0) {
    return variationOutputCount;
  }

  const bvhURLs = motion.bvhURLs ?? motion.bvh_urls ?? [];
  const nonEmptyBvhs = bvhURLs.map((url) => url.trim()).filter(Boolean);
  if (nonEmptyBvhs.length > 0) {
    return nonEmptyBvhs.length;
  }

  const mhrBvhs =
    motion.mhrBvhURLs?.map((url) => url.trim()).filter(Boolean) ?? [];
  if (mhrBvhs.length > 0) {
    return mhrBvhs.length;
  }

  if (motion.bvhURL?.trim()) {
    return 1;
  }

  const exportURLs = parseMotionExportURLSlots(
    motion.exportURLs ?? motion.export_urls,
  );
  const fromExports = previewBvhUrlsFromExportSlots(exportURLs);
  if (fromExports.length > 0) {
    return fromExports.length;
  }

  return 1;
}

export function countJobVariationOutputs(
  job: Pick<Job, 'variationInfoByID'> | null | undefined,
  variationNumber: number,
) {
  const variation = getJobVariation(job, variationNumber);
  if (!variation) {
    return 1;
  }

  const variationOutputCount = countVariationOutputSlots(variation.outputs);
  if (variationOutputCount > 0) {
    return variationOutputCount;
  }

  return 1;
}

function getJobVariation(
  job: Pick<Job, 'variationInfoByID'> | null | undefined,
  variationNumber: number,
): Variation | null {
  const variations = job?.variationInfoByID;
  if (!variations) {
    return null;
  }

  const variationKey = String(variationNumber);
  return (
    variations[variationKey] ??
    Object.values(variations).find(
      (variation) => Number(variation.variationID) === variationNumber,
    ) ??
    Object.values(variations)[0] ??
    null
  );
}

function countVariationOutputSlots(
  outputs: readonly VariationOutput[] | undefined,
) {
  if (!outputs?.length) {
    return 0;
  }

  const playableOutputCount = outputs.filter((output) =>
    output?.bvh?.trim(),
  ).length;
  return playableOutputCount > 0 ? playableOutputCount : outputs.length;
}
